import { useState, useMemo, useEffect } from 'react';
import { useDENTContext } from '../../../Services/DENTContext';
import { PathologyItem } from '../../../Enums/PathologyItem.enum';
import { SurfaceHandler } from '../../../Interfaces/SurfaceHandler.interface';
import useDecaySurfaceHandler from './useDecaySurfaceHandler';
import useToothWearSurfaceHandler from './useToothWearSurfaceHandler';
import {
	isSurfaceBasedToothWearType,
	ToothWearType,
} from '../../../Components/DENTModalOverviewPathologyButtons/DENTModalOverviewPathologyToothWear';

const EMPTY_SURFACE_HANDLER: SurfaceHandler = {
	showedSurfaces: [],
	selectedSurfaces: [],
	handleSelectSurface: () => {},
	shouldShowSurfaces: false,
};

const resolveToothWearType = (
	pathologyType: PathologyItem,
	toothWearType?: ToothWearType,
) => {
	if (pathologyType === PathologyItem.EROSION) {
		return ToothWearType.EROSION;
	}

	if (
		pathologyType === PathologyItem.TOOTH_WEAR &&
		toothWearType &&
		isSurfaceBasedToothWearType(toothWearType)
	) {
		return toothWearType;
	}

	return undefined;
};

export default function usePathologySurfaceHandler(
	pathologyType: PathologyItem,
	toothWearType?: ToothWearType,
): SurfaceHandler {
	const { activeTooth } = useDENTContext();

	const [decayIndex, setDecayIndex] = useState(
		activeTooth?.pathology?.decays?.length ?? 0,
	);

	useEffect(() => {
		const decaysLength = activeTooth?.pathology?.decays?.length ?? 0;
		if (decayIndex >= decaysLength) {
			setDecayIndex(decaysLength);
		}
	}, [activeTooth?.pathology?.decays?.length, decayIndex]);

	const decayHandler = useDecaySurfaceHandler(decayIndex);
	const resolvedToothWearType = resolveToothWearType(
		pathologyType,
		toothWearType,
	);
	const toothWearHandler = useToothWearSurfaceHandler(resolvedToothWearType);

	return useMemo(() => {
		switch (pathologyType) {
			case PathologyItem.DECAY:
				return {
					...decayHandler,
					decayIndex,
					setDecayIndex,
				} as SurfaceHandler;

			case PathologyItem.TOOTH_WEAR:
			case PathologyItem.EROSION:
				if (!resolvedToothWearType) {
					return EMPTY_SURFACE_HANDLER;
				}

				return { ...toothWearHandler, shouldShowSurfaces: true };

			default:
				return EMPTY_SURFACE_HANDLER;
		}
	}, [
		pathologyType,
		resolvedToothWearType,
		decayIndex,
		decayHandler,
		toothWearHandler,
	]);
}
